// 仿真绑定状态机 / Simulation binding state machine.
// 负责：启动时拉 /active → 若有 running → 拉 detail timeline + 开 SSE；
// 若只有 latest → 拉 detail timeline，状态条显示终态；
// 都没有 → vacant。启动新仿真时切换绑定。
// Drives idle → loading → vacant / bound_history / bound_live → bound_done (bound_history).

import type { Mode, SimulationDetail, SimulationRow, SimulationStatus } from "@/lib/types";
import { state as appState } from "@/state/store";
import { sseEventToEntry, timelineItemToEntry } from "@/features/log-terminal/event-mapper";
import {
  clearTab,
  replayEntries,
  appendEntry,
} from "@/features/log-terminal/terminal-buffer";
import type {
  ActiveResponse,
  BindingState,
  SseEvent,
  TimelineItem,
} from "@/features/log-terminal/terminal-types";

// 注入依赖，便于隔离测试 / Dependency-injected fetchers for test isolation.
export interface BinderDeps {
  fetchActive: () => Promise<ActiveResponse>;
  fetchDetail: (
    id: string,
  ) => Promise<{ ok: true; simulation: SimulationDetail & { timeline?: TimelineItem[] } }>;
  openSse: (
    id: string,
    handlers: { onEvent: (ev: SseEvent) => void; onError?: (err: unknown) => void },
  ) => { close: () => void };
}

type Listener = (state: BindingState) => void;

// 终态常量：SimulationStatus 终态集合，以及对应的 SSE run.* 事件集合。
// Terminal status constants: shared by startSse short-circuit and SSE terminal detection.
const TERMINAL_STATUSES = ["completed", "failed", "cancelled", "timeout"] as const;
const TERMINAL_RUN_EVENTS = new Set(TERMINAL_STATUSES.map((s) => `run.${s}`));

let state: BindingState = { kind: "idle" };
const listeners = new Set<Listener>();
let sseHandle: { close: () => void } | null = null;
let deps: BinderDeps | null = null;

function transition(next: BindingState): void {
  state = next;
  syncReportGenerationState(next);
  for (const fn of listeners) fn(state);
}

/** 外部读当前绑定状态 / Current binding state. */
export function currentBinding(): BindingState {
  return state;
}

/** 订阅绑定状态变化 / Subscribe to binding transitions. */
export function subscribeBinding(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** 启动 binder：SPA 进入工作台时调用一次 / Kick off the binder at console entry. */
export async function bootstrapBinder(injected: BinderDeps): Promise<void> {
  deps = injected;
  transition({ kind: "loading" });
  try {
    const body = await deps.fetchActive();
    if (body.simulation) {
      await bindToRunning(body.simulation);
      return;
    }
    if (body.latest) {
      await bindToHistory(body.latest);
      return;
    }
    transition({ kind: "vacant" });
  } catch (error) {
    console.error("log-terminal bootstrap failed", error);
    transition({ kind: "vacant" });
  }
}

/** 用户启动新仿真 / Called after startTaskSimulation / startDemoSimulation returns. */
export async function onSimulationStarted(simulationId: string, mode: Mode): Promise<void> {
  if (!deps) return;
  closeSse();
  clearTab("planA");
  clearTab("planB");
  try {
    // 拉 detail → 回放 timeline → 开 SSE；任一步失败则回到 vacant 并把错误抛给调用方。
    // Fetch detail → replay timeline → start SSE; on any failure, recover to vacant and rethrow.
    const detail = await deps.fetchDetail(simulationId);
    replayTimeline(detail.simulation.timeline ?? []);
    startSse(simulationId, mode, detail.simulation.status, detail.simulation.report_status);
  } catch (error) {
    console.error("onSimulationStarted failed", error);
    transition({ kind: "vacant" });
    throw error;
  }
}

// 同步的 reset，供测试 / Test-only reset.
export function resetBinder(): void {
  closeSse();
  deps = null;
  state = { kind: "idle" };
  appState.reportGeneration = null;
  listeners.clear();
}

// ────────────────────────── internals ──────────────────────────

async function bindToRunning(sim: SimulationRow): Promise<void> {
  if (!deps) return;
  const detail = await deps.fetchDetail(sim.id);
  replayTimeline(detail.simulation.timeline ?? []);
  startSse(sim.id, sim.mode, detail.simulation.status, detail.simulation.report_status);
}

async function bindToHistory(sim: SimulationRow): Promise<void> {
  if (!deps) return;
  const detail = await deps.fetchDetail(sim.id);
  replayTimeline(detail.simulation.timeline ?? []);
  if (shouldFollowPendingReport(detail.simulation)) {
    startSse(sim.id, sim.mode, detail.simulation.status, detail.simulation.report_status);
    return;
  }
  transition({
    kind: "bound_history",
    simulationId: sim.id,
    mode: sim.mode,
    status: sim.status,
  });
}

function replayTimeline(items: TimelineItem[]): void {
  const entries = items
    .map((item) => timelineItemToEntry(item))
    .filter((e): e is NonNullable<typeof e> => e !== null);
  replayEntries(entries);
}

function startSse(
  simulationId: string,
  mode: Mode,
  status: string,
  reportStatus?: string,
): void {
  if (!deps) return;

  // 若已终态直接落 bound_history 不开 SSE。
  // If already terminal, land in bound_history without opening SSE.
  if (status === "completed" && reportStatus === "pending") {
    transition({ kind: "bound_report", simulationId, mode });
  } else if ((TERMINAL_STATUSES as readonly string[]).includes(status)) {
    transition({
      kind: "bound_history",
      simulationId,
      mode,
      status: status as SimulationStatus,
    });
    return;
  } else {
    transition({ kind: "bound_live", simulationId, mode });
  }

  sseHandle = deps.openSse(simulationId, {
    onEvent: (ev) => {
      const entry = sseEventToEntry(ev, { now: Date.now() });
      if (entry) appendEntry(entry);
      if (ev.type === "report.started") {
        transition({ kind: "bound_report", simulationId, mode });
      }
      if (ev.type === "report.regenerated" || ev.type === "report.failed") {
        closeSse();
        transition({ kind: "bound_history", simulationId, mode, status: "completed" });
      }
      if (TERMINAL_RUN_EVENTS.has(ev.type)) {
        closeSse();
        transition({
          kind: "bound_history",
          simulationId,
          mode,
          status: ev.type.slice("run.".length) as SimulationStatus,
        });
      }
    },
  });
}

function shouldFollowPendingReport(sim: SimulationDetail): boolean {
  return sim.status === "completed" && sim.report_status === "pending";
}

function closeSse(): void {
  sseHandle?.close();
  sseHandle = null;
}

function syncReportGenerationState(next: BindingState): void {
  if (next.kind === "bound_report") {
    appState.reportGeneration = {
      simulationId: next.simulationId,
      taskId: null,
      jobId: null,
      status: "pending",
    };
    return;
  }
  if (next.kind === "vacant") {
    appState.reportGeneration = null;
    return;
  }
  if (
    appState.reportGeneration &&
    "simulationId" in next &&
    next.simulationId === appState.reportGeneration.simulationId
  ) {
    appState.reportGeneration = null;
  }
}
